import React from "react";
import { type SeoCluster } from "@shared/config/seo-pages";

import { getFeaturesVariant } from "./features-by-cluster";

interface FeaturesSectionProps {
  /** Тематическая группа страницы — определяет набор пунктов. Без неё показывается общий вариант */
  cluster?: SeoCluster;
}

/** Мокап транскрипта — статичная презентация результата (без логики и данных) */
const TranscriptMock: React.FC = () => {
  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-[0_12px_32px_-20px_rgba(0,0,0,0.25)]">
      <div className="mb-4 flex items-center justify-between border-b border-border pb-4">
        <span className="font-semibold">Интервью_с_архитектором</span>
        <span className="text-xs text-muted-foreground">mp3 · 42:10 · качество</span>
      </div>

      <div className="grid grid-cols-[54px_1fr] gap-3 py-3">
        <span className="pt-0.5 font-mono text-xs text-muted-foreground">00:12</span>
        <div>
          <span className="mb-1.5 inline-block rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700">
            Интервьюер
          </span>
          <p className="text-base">Расскажите, с чего начался проект?</p>
        </div>
      </div>

      <div className="grid grid-cols-[54px_1fr] gap-3 border-t border-border py-3">
        <span className="pt-0.5 font-mono text-xs text-muted-foreground">02:31</span>
        <div>
          <span className="mb-1.5 inline-block rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
            Архитектор
          </span>
          <p className="text-base">Всё началось с двух реконструкций старых домов в центре города…</p>
        </div>
      </div>

      <div className="grid grid-cols-[54px_1fr] gap-3 border-t border-border py-3">
        <span className="pt-0.5 font-mono text-xs text-muted-foreground">05:04</span>
        <div>
          <span className="mb-1.5 inline-block rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700">
            Интервьюер
          </span>
          <p className="text-base">А как решали вопрос с шумоизоляцией?</p>
        </div>
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">AI-сводка</p>
        <ul className="space-y-2">
          <li className="pl-4 text-sm text-foreground">Проект вырос из двух реконструкций старых домов.</li>
          <li className="pl-4 text-sm text-foreground">Ключевое решение — собственная сборка перекрытий.</li>
        </ul>
      </div>
    </div>
  );
};

export const FeaturesSection: React.FC<FeaturesSectionProps> = ({ cluster }) => {
  const { heading, subtitle, features } = getFeaturesVariant(cluster);

  return (
    <section id="features" className="border-b border-border bg-background py-16 md:py-24">
      <div className="container px-4 md:px-6">
        <div className="mb-10">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Что вы получаете</p>
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{heading}</h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">{subtitle}</p>
        </div>

        <div className="grid items-start gap-12 md:grid-cols-2">
          <TranscriptMock />

          <div className="grid gap-6">
            {features.map(feature => (
              <div key={feature.title} className="border-t border-border pt-5">
                <h3 className="mb-1.5 text-lg font-semibold text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
